import { styled } from '@byecode/ui/theme/stitches.config'
import React, { useMemo, useState } from 'react'
import type { Root } from 'react-dom/client'
import { createRoot } from 'react-dom/client'

import type { ButtonProps } from '../Button'
import { Button } from '../Button'
import { Checkbox } from '../Checkbox'
import { Input } from '../Input'
import { Text } from '../Text'
import type { BaseModalProps } from './base'
import { DesktopModal } from './desktop'

const ROOT = '__BUI__REACT__ROOT__'
type Container = Element & { [ROOT]?: Root }

const Title = styled('div', {
    marginBottom: 12,
    color: '$colorBlack',
    fontSize: '16px',
    lineHeight: '20px',
    fontWeight: 600
})

const Footer = styled('div', {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px'
})

const FooterLeft = styled('div', {
    flex: 1
})

const FooterRight = styled('div', {
    display: 'flex',
    gap: '8px'
})

const ContentContainer = styled('div', {
    lineHeight: '22px',
    color: '$colorGray600'
})

export interface ValidationConfig {
    requireInputMatch?: boolean // 是否需要输入匹配
    expectedValue?: string // 期望输入值（当requireInputMatch为true时使用）
    placeholder?: string // 输入框占位符
    inputLabel?: string // 输入框标签

    requireCheckbox?: boolean // 是否需要checkbox确认
    checkboxLabel?: React.ReactNode // checkbox标签
    checkBoxPosition?: 'footer' | 'content'
}

export interface ConfirmProps extends Omit<BaseModalProps, 'children' | 'title' | 'content' | 'open' | 'withCloseIcon' | 'footer'> {
    title: string
    content?: React.ReactNode
    okText?: React.ReactNode
    okStatus?: 'error' | 'primary'
    okProps?: ButtonProps
    cancelText?: React.ReactNode
    okDisable?: boolean
    extra?: React.ReactNode
    withCancel?: boolean
    footerExtra?: React.ReactNode | ((params: { onReject: () => void; onResolve: () => void; onUnmount?: () => void }) => React.ReactNode)
    onCheckChange?: (checked: boolean) => void
    // 新增：验证配置，让Modal内部管理状态
    validation?: ValidationConfig
}

export interface ModalConfirmProps extends ConfirmProps {
    onReject: () => void
    onResolve: () => void
    onUnmount?: () => void
}

export const ModalConfirm: React.FC<ModalConfirmProps> = cfg => {
    const {
        content,
        title,
        okText,
        okStatus = 'error',
        okProps,
        cancelText,
        okDisable,
        extra,
        footerExtra,
        withCancel = true,
        onReject,
        onResolve,
        onUnmount,
        onCheckChange,
        validation,
        ...props
    } = cfg
    const [checked, setChecked] = useState(false)
    const [inputValue, setInputValue] = useState('')

    const handleCheckBoxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newChecked = e.target.checked
        setChecked(newChecked)
        onCheckChange?.(newChecked)
    }

    const okButtonProps = useMemo(() => (okStatus === 'error' ? { ...okProps, danger: true } : okProps), [okProps, okStatus])

    const disabled = useMemo(() => {
        // 如果有validation配置，使用validation逻辑
        if (validation) {
            let isValid = true

            // 检查checkbox是否必需且已选中
            if (validation.requireCheckbox && !checked) {
                isValid = false
            }

            // 检查输入值是否匹配期望值
            if (validation.requireInputMatch && inputValue !== validation.expectedValue) {
                isValid = false
            }

            return !isValid
        }

        // 原有逻辑 - 已移除checkbox确认功能，统一使用validation配置
        return okDisable
    }, [checked, okDisable, validation, inputValue])

    const checkBoxInFooter = validation?.checkBoxPosition === 'footer'

    return (
        <DesktopModal
            open
            width={360}
            withCloseIcon={false}
            closeOnClickOverlay={false}
            onClose={onReject}
            styles={{
                body: {
                    padding: '20px 20px 0',
                    fontSize: 14,
                    color: '$colorGray600',
                    overflow: 'hidden'
                },
                footer: {
                    padding: '24px 20px'
                }
            }}
            footer={
                <Footer>
                    {(footerExtra || checkBoxInFooter) && (
                        <FooterLeft>
                            {checkBoxInFooter && validation?.requireCheckbox && (
                                <Checkbox
                                    style={{ color: 'var(--color-gray-500)' }}
                                    checked={checked}
                                    size="xs"
                                    label={validation?.checkboxLabel}
                                    onChange={handleCheckBoxChange}
                                />
                            )}
                            {typeof footerExtra === 'function' ? footerExtra({ onResolve, onReject, onUnmount }) : footerExtra}
                        </FooterLeft>
                    )}
                    <FooterRight>
                        {withCancel && (
                            <Button size="lg" onClick={onReject}>
                                {cancelText || '取消'}
                            </Button>
                        )}
                        <Button size="lg" type="primary" {...okButtonProps} disabled={disabled} onClick={onResolve}>
                            {okText || '删除'}
                        </Button>
                    </FooterRight>
                </Footer>
            }
            {...props}
        >
            <Title>{title}</Title>
            <ContentContainer>{content}</ContentContainer>
            {validation?.requireInputMatch && (
                <div style={{ marginTop: 12 }}>
                    {validation.inputLabel && (
                        <Text color="#000" style={{ marginBottom: 8 }}>
                            {validation.inputLabel}
                        </Text>
                    )}
                    <Input
                        size="lg"
                        placeholder={validation.placeholder}
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                    />
                </div>
            )}
            {extra}
            {!checkBoxInFooter && validation?.requireCheckbox && (
                <Checkbox
                    style={{ marginTop: 12, color: 'var(--color-gray-500)' }}
                    checked={checked}
                    size="xs"
                    label={validation?.checkboxLabel}
                    onChange={handleCheckBoxChange}
                />
            )}
        </DesktopModal>
    )
}

export const confirm = (cfg: ConfirmProps | ((ok: () => void, cancel: () => void) => ConfirmProps)) => {
    const container = document.createDocumentFragment() as unknown as Container
    const root = container[ROOT] || createRoot(container)
    container[ROOT] = root

    return new Promise<boolean>(resolve => {
        const handlerResolve = () => {
            root.unmount()
            resolve(true)
        }

        const handleReject = () => {
            root.unmount()
            resolve(false)
        }

        const handleUnmount = () => {
            root.unmount()
        }

        const props = typeof cfg === 'function' ? cfg(handlerResolve, handleReject) : cfg

        root.render(<ModalConfirm onReject={handleReject} onResolve={handlerResolve} onUnmount={handleUnmount} {...props} />)
    })
}
